/**
 * A v4 UUID, in a context that may not be secure. Story VC-016.
 *
 * # Why this file exists
 *
 * `crypto.randomUUID()` is restricted to secure contexts. A page served over
 * plain HTTP on a LAN address — which is how this client is reached from a
 * phone on the same network — has `crypto` and does *not* have
 * `randomUUID`, so calling it throws `TypeError`.
 *
 * That failure is silent in the worst way. The id is minted on the first
 * line of sending a message, before the bubble is rendered and before the
 * request is built, so the whole action disappears: nothing is posted,
 * nothing appears on screen, and the text is put back in the box. It looks
 * exactly like a button that is not wired to anything, and it cannot be
 * reproduced on `localhost`, which *is* a secure context.
 *
 * `crypto.getRandomValues` carries no such restriction — it is not gated on
 * secure context — so the fallback is a real v4 UUID from real random bytes
 * rather than a weaker id. `Math.random` is deliberately not in this file:
 * §9 makes the message id an idempotency key, and two of them colliding is
 * one of two messages silently becoming one.
 */

/**
 * @returns {string} a version-4 UUID
 */
export function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // RFC 9562 §5.4: version in the high nibble of octet 6, variant in the two
  // high bits of octet 8. A server that parses this as a UUID will reject
  // one with the wrong bits set, so they are set rather than left to chance.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
